import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export const ProjektePreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const projects = [
    {
      title: "Bürokomplex Westpark",
      category: "Gewerbebau",
      color: "blue",
      image: "/images/projekt-buero.jpg",
      area: "Bauunternehmung",
    },
    {
      title: "Gartenanlage Villa Sonnenhof",
      category: "Privatgarten",
      color: "green",
      image: "/images/galabau-hero.jpg",
      area: "Galabau",
    },
    {
      title: "Wertgutachten Stadtmitte 12",
      category: "Immobilienbewertung",
      color: "orange",
      image: "/images/sv-hero.jpg",
      area: "Sachverständiger",
    },
    {
      title: "Mehrfamilienhaus Braunschweig",
      category: "Wohnungsbau",
      color: "blue",
      image: "/images/projekt-wohnbau.jpg",
      area: "Bauunternehmung",
    },
  ];

  const colorMap = {
    blue: { badge: "bg-brand-blue-muted text-brand-blue-light", line: "gradient-blue" },
    green: { badge: "bg-brand-green-muted text-brand-green-light", line: "gradient-green" },
    orange: { badge: "bg-brand-orange-muted text-brand-orange-light", line: "gradient-orange" },
  };

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="max-w-xl"
          >
            <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
              Ausgewählte Projekte
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4 leading-tight">
              Referenzen die überzeugen.
            </h2>
            <p className="text-sm text-muted-foreground font-body leading-relaxed">
              Ein Auszug unserer erfolgreich abgeschlossenen Projekte aus allen drei Geschäftsbereichen.
            </p>
          </motion.div>
          <Button variant="outline" size="lg" asChild>
            <Link to="/">
              Alle Projekte ansehen
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>

        {/* Projects grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
          {projects.map((project, index) => {
            const colors = colorMap[project.color];
            return (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="group cursor-pointer"
              >
                <div className="bg-background overflow-hidden h-full flex flex-col">
                  <div className={`h-0.5 ${colors.line}`} />
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center group-hover:scale-105"
                      style={{
                        backgroundImage: `url(${project.image})`,
                        transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    />
                    <div className="absolute inset-0 bg-background/30 group-hover:bg-background/10" style={{ transition: 'background-color 0.3s' }} />
                    {/* Category badge */}
                    <div className="absolute top-4 left-4">
                      <span className={`text-[10px] px-3 py-1 font-heading uppercase tracking-wider ${colors.badge}`}>
                        {project.area}
                      </span>
                    </div>
                  </div>
                  {/* Info */}
                  <div className="p-6">
                    <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-2">
                      {project.category}
                    </p>
                    <h3 className="font-heading font-semibold text-base text-foreground group-hover:text-brand-blue-light" style={{ transition: 'color 0.2s' }}>
                      {project.title}
                    </h3>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
